import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';

const Login = ({ setAuthHeaders, backEnd }) => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [manualResetDialog, setManualResetDialog] = useState(false);
  const [resetInProgress, setResetInProgress] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const navigate = useNavigate();
   useEffect(() => {
    const smartvalString = localStorage.getItem('smartval');
    // const smartval = JSON.parse(smartvalString)

    if (smartvalString) {
      // Redirect to a static location:
      // navigate('/dashboard');

      // OR redirect dynamically based on smartval:
      setAuthHeaders(smartvalString);
      navigate('/my-menu');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(backEnd + 'login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseBody = await response.json();

      if (responseBody.user === 'User not found') {
        setOpenDialog(true);
        return;
      }

      const authHeader = response.headers.get('Authorization');
      localStorage.setItem('smartval', authHeader)
      setAuthHeaders(authHeader);
      navigate('/my-menu');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handlePasswordReset = async () => {
    setResetInProgress(true);
    try {
      const response = await fetch(backEnd + 'password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`Password reset failed! Status: ${response.status}`);
      }

      await response.json();

      setSnackbarMessage('На посочения мейл адрес са изпратени допълнителни инструкции за смяна на парола');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setOpenDialog(false);
      setManualResetDialog(false);
      navigate('/login');
    } catch (error) {
      console.error('Password reset failed:', error);
      setSnackbarMessage('Грешка при възстановяване на парола!');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResetInProgress(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 'bold',
          textAlign: 'center'
        }}
      >
        Вход
      </Typography>

      <form onSubmit={handleLogin}>
        <TextField
          label="Мейл"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          sx={{
            '& label': { color: 'gray' },
            '& label.Mui-focused': { color: theme.palette.text.secondary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: theme.palette.text.secondary },
              '&:hover fieldset': { borderColor: theme.palette.secondary.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.primary.secondary },
            },
          }}
        />
        <TextField
          label="Парола"
          type="password"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          sx={{
            '& label': { color: 'gray' },
            '& label.Mui-focused': { color: theme.palette.text.secondary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: theme.palette.text.secondary },
              '&:hover fieldset': { borderColor: theme.palette.secondary.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.primary.secondary },
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            mt: 2,
            backgroundColor: theme.palette.text.primary,
            color: 'white',
            '&:hover': {
              backgroundColor: 'white',
              color: theme.palette.text.primary,
            },
          }}
        >
          Вход
        </Button>
      </form>

      <Typography
        onClick={() => setManualResetDialog(true)}
        sx={{
          mt: 2,
          textAlign: 'center',
          color: theme.palette.text.primary,
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: '0.95rem'
        }}
      >
        Забравена парола?
      </Typography>

      {/* Dialog for login failure */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Грешка във входните данни</DialogTitle>
        <DialogContent>
          <Typography>Желаете ли да възстановите паролата си?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Затвори</Button>
          <Button
            onClick={handlePasswordReset}
            color="primary"
            variant="contained"
            disabled={resetInProgress}
          >
            {resetInProgress ? 'Изпращам...' : 'Възстанови парола'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for manual reset from link */}
      <Dialog open={manualResetDialog} onClose={() => setManualResetDialog(false)}>
        <DialogTitle>Забравена парола</DialogTitle>
        <DialogContent>
          <TextField
            label="Мейл"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{
            '& label': { color: 'gray' },
            '& label.Mui-focused': { color: theme.palette.text.secondary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: theme.palette.text.secondary },
              '&:hover fieldset': { borderColor: theme.palette.secondary.main },
              '&.Mui-focused fieldset': { borderColor: theme.palette.primary.secondary },
            },
          }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualResetDialog(false)}
           sx={{
            mt: 2,
            backgroundColor: theme.palette.text.primary,
            color: 'white',
            '&:hover': {
              backgroundColor: 'white',
              color: theme.palette.text.primary,
            },
          }}
            
            >Затвори</Button>
          <Button
            onClick={handlePasswordReset}
            color="primary"
            variant="contained"
            disabled={resetInProgress}
            sx={{
            mt: 2,
            backgroundColor: theme.palette.text.primary,
            color: 'white',
            '&:hover': {
              backgroundColor: 'white',
              color: theme.palette.text.primary,
            },
          }}
          >
            {resetInProgress ? 'Изпращам...' : 'Изпрати'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
